// src/encounter/lib/lab-aliases.ts
// Physicians' common lab names → the CPT code the master lab catalogue is keyed on (PROD-2795).
//
// The master catalogue (GET /api/Master/LabOrderCodesList) matches only `Code.Contains(q) ||
// Display.Contains(q)` over short CPT descriptions, so "HbA1c" finds nothing while "glycosylated"
// does. This table lets the lab search ALSO look the test up by its CPT code. Deterministic: no
// model, no ranking guesswork. A query with no alias simply gets the plain text search, and a code
// the catalogue does not hold returns nothing — no row is ever invented from this table.

/**
 * Lower-case and drop the separators clinicians type inconsistently (spaces, hyphens/dashes,
 * brackets, dots, slashes), so "Lp(a)" → "lpa", "HbA1c" → "hba1c", "25-OH D" → "25ohd",
 * "hs-CRP" → "hscrp".
 */
export function normaliseLabQuery(q: string): string {
  return q.toLowerCase().replace(/[\s\-‐-―()[\]{}./\\]/g, '');
}

// CPT, standard; presence in the dev catalogue probed at merge time
export const LAB_ALIASES: Readonly<Record<string, string>> = {
  cbc: '85025',
  fbc: '85025',
  fullbloodcount: '85025',
  completebloodcount: '85025',
  cmp: '80053',
  chem14: '80053',
  comprehensivemetabolicpanel: '80053',
  bmp: '80048',
  chem7: '80048',
  basicmetabolicpanel: '80048',
  lft: '80076',
  liverpanel: '80076',
  liverfunction: '80076',
  kft: '80069',
  rft: '80069',
  renalpanel: '80069',
  renalfunction: '80069',
  lipidpanel: '80061',
  lipids: '80061',
  lipidprofile: '80061',
  electrolytes: '80051',
  lytes: '80051',
  tsh: '84443',
  ft4: '84439',
  freet4: '84439',
  ft3: '84481',
  freet3: '84481',
  glucose: '82947',
  fbg: '82947',
  fastingglucose: '82947',
  fbs: '82947',
  creatinine: '82565',
  hdl: '83718',
  triglycerides: '84478',
  tg: '84478',
  cholesterol: '82465',
  totalcholesterol: '82465',
  alt: '84460',
  sgpt: '84460',
  ast: '84450',
  sgot: '84450',
  hba1c: '83036',
  a1c: '83036',
  hemoglobina1c: '83036',
  haemoglobina1c: '83036',
  glycatedhemoglobin: '83036',
  glycatedhaemoglobin: '83036',
  apob: '82172',
  apolipoproteinb: '82172',
  lpa: '83695',
  lipoproteina: '83695',
  hscrp: '86141',
  crp: '86140',
  vitd: '82306',
  vitamind: '82306',
  '25ohd': '82306',
  '25ohvitamind': '82306',
  b12: '82607',
  vitaminb12: '82607',
  folate: '82746',
  ferritin: '82728',
  iron: '83540',
  tibc: '83550',
  fastinginsulin: '83525',
  insulin: '83525',
  uricacid: '84550',
  homocysteine: '83090',
  psa: '84153',
  testosterone: '84403',
  // UACR / albumin-creatinine ratio are deliberately NOT aliased: the ratio is 82043 + 82570, so
  // 82043 alone would lead with an albumin-only order (Codex review, PROD-2795).
  microalbumin: '82043',
  magnesium: '83735',
  ggt: '82977',
  esr: '85652',
  ua: '81001',
  urinalysis: '81001',
  cortisol: '82533',
  dheas: '82627',
  estradiol: '82670',
};

const PREFIX_MIN_LENGTH = 3;
const MAX_ALIAS_CODES = 3;

/** The CPT code of the EXACT normalised alias, if the query is one. Own keys only. */
export function exactLabAlias(q: string, table: Readonly<Record<string, string>> = LAB_ALIASES): string | undefined {
  const n = normaliseLabQuery(q);
  // Own keys only: "constructor" / "toString" must never resolve to Object.prototype members.
  return n && Object.hasOwn(table, n) ? table[n] : undefined;
}

/**
 * The CPT code(s) a typed lab query stands for: the exact normalised alias first, then (for 3+
 * normalised characters, so "hba1" works mid-typing) every alias that starts with the query, in
 * table order. Distinct codes, at most 3. Unknown → []. `table` is for tests only.
 */
export function lookupLabAlias(q: string, table: Readonly<Record<string, string>> = LAB_ALIASES): string[] {
  const n = normaliseLabQuery(q);
  if (!n) return [];
  const codes: string[] = [];
  const add = (code: string) => {
    if (codes.length < MAX_ALIAS_CODES && !codes.includes(code)) codes.push(code);
  };
  const exact = exactLabAlias(n, table);
  if (exact) add(exact);
  if (n.length >= PREFIX_MIN_LENGTH) {
    for (const [alias, code] of Object.entries(table)) {
      if (alias.startsWith(n)) add(code);
    }
  }
  return codes;
}

/**
 * Does a catalogue row's code really carry the alias's CPT code? The backend matches `searchQuery`
 * as a SUBSTRING of code OR display, so a code search can return rows that merely mention the
 * digits. Accept the exact code, or the code followed by a non-digit suffix (a modifier).
 */
export function isAliasCodeMatch(rowCode: string | null | undefined, cpt: string): boolean {
  const c = (rowCode ?? '').trim().toUpperCase();
  if (!c.startsWith(cpt)) return false;
  return c.length === cpt.length || !/\d/.test(c.charAt(cpt.length));
}

/**
 * CPT codes that bill a CLASS of analytes, not one test: the code alone does not say which test a
 * catalogue row is. 82172 is "Apolipoprotein, each" — A1 and B both carry it.
 */
const MULTI_ANALYTE_CPT: ReadonlySet<string> = new Set(['82172']);

/**
 * The catalogue rows an alias code may add. For a single-test code, every row that carries it.
 * For a multi-analyte code, only rows whose display NAMES one of the code's aliases — so "ApoB"
 * can surface "APOLIPOPROTEIN B" but never "APOLIPOPROTEIN A1" or a bare "APOLIPOPROTEIN, EACH"
 * (those stay findable by typing their text). Never reorders.
 */
export function aliasRowsFor<T extends { display: string }>(
  rows: T[],
  cpt: string,
  table: Readonly<Record<string, string>> = LAB_ALIASES,
): T[] {
  if (!MULTI_ANALYTE_CPT.has(cpt)) return rows;
  const names = Object.entries(table).flatMap(([alias, code]) => (code === cpt ? [alias] : []));
  return rows.filter((r) => names.some((a) => normaliseLabQuery(r.display).includes(a)));
}
